import { eventKeys } from "@/lib/eventKeys";
import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import customViewTodoMapper from "@/mappers/customView/customViewTodoMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import useGetEventBus from "../useGetEventBus";
import useFetchErrorNotifier from "../utils/useFetchErrorNotifier";

// Fetches todos for the Smart Todos custom view widget.
// Fetch/event logic would mirror a future mobile custom view todos hook.
const useGetCustomViewTodos = (viewId?: string, enabled = true) => {
    const { customViewManager } = useSdk();
    const notifyFetchError = useFetchErrorNotifier();
    const queryClient = useQueryClient();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const viewIdRef = useRef(viewId);
    viewIdRef.current = viewId;

    const todos = useQuery({
        queryKey: queryKeys.customView.todos(viewId!),
        queryFn: async () => {
            try {
                if (!viewId) {
                    throw new Error("View ID is undefined");
                }

                const viewTodos = await customViewManager.getViewTodos(viewId);

                // Backend rule: accepted todos + proposed todos from the last 7 days.
                // Widget only shows pending todos, consistent with the dashboard widget.
                return viewTodos
                    .filter((todo) => !todo.done)
                    .map(customViewTodoMapper);
            } catch (error) {
                notifyFetchError({
                    queryKey: queryKeys.customView.todos(viewId!),
                    titleTranslationKey: "errors.getTodos",
                });
                throw error;
            }
        },
        enabled: !!viewId && enabled,
        staleTime: STALE_TIME.realtime,
    });

    const invalidateCustomViewTodos = () => {
        const currentViewId = viewIdRef.current;
        if (!currentViewId) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.customView.todos(currentViewId),
            });
            timeoutRef.current = null;
        }, 2300);
    };

    const { off: offTodosUpdated } = useGetEventBus(
        eventKeys.todos.updated,
        invalidateCustomViewTodos,
    );

    const { off: offMailUpdated } = useGetEventBus(
        eventKeys.mails.updated,
        invalidateCustomViewTodos,
    );

    const { off: offListUpdated } = useGetEventBus(
        eventKeys.customView.listUpdated,
        invalidateCustomViewTodos,
    );

    useEffect(
        () => () => {
            offTodosUpdated(eventKeys.todos.updated, invalidateCustomViewTodos);
            offMailUpdated(eventKeys.mails.updated, invalidateCustomViewTodos);
            offListUpdated(
                eventKeys.customView.listUpdated,
                invalidateCustomViewTodos,
            );
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    return todos;
};

export default useGetCustomViewTodos;
